import React, { memo, ReactElement, ReactNode } from 'react';
import { LoggableProps, useLogger } from '@direct-frontend/modules-logger';
import { getPlatformStyles, isDesktop, isMobile } from '@direct-frontend/platform-code';

import { addRootNodeClassName, cn } from '../../lib/css/className/className';
import { HStack, VStack } from '../Stack';
import { SkinSize } from '../../lib/skin/getSizeSkin/generated/types';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { useInvertedTheme, useTheme } from '../../lib/theme/ThemeProvider';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { TextPresetWithSkin } from '../TextGeneric/types';
import {
    InfoBaseText,
    InfoBaseButtons,
    InfoBaseCloseButton,
    InfoBaseIllustration,
    InfoBaseIcon,
    InfoBaseImage,
    InfoBaseBottomText,
    InfoBasePropsWithType,
    InfoColor,
    addImageBlockWrapper,
    createInfoColorConfig,
    getContainerGap,
} from '../InfoBase';

import { i18n } from './InfoBlock.i18n';

getPlatformStyles({
    common: import('./InfoBlock.css'),
    mobile: import('./InfoBlock@mobile.css'),
    desktop: import('./InfoBlock@desktop.css'),
});

const cls = cn('InfoBlock');

export type InfoBlockColor = InfoColor;

// TODO DIRECT-222878 Передавать массив actions и из него формировать кнопки и меню (когда появится меню)?
export type InfoBlockPropsRaw = InfoBasePropsWithType & {
    /**
     * Цвет элемента
     * @default island
     */
    color?: InfoBlockColor;

    /**
     * Компонент лейбла справа от заголовка и caption.
     * @expectedNode `Label` | `LabelStub`
     */
    labelBlock?: ReactNode;
};

export type InfoBlockProps = LoggableProps<InfoBlockPropsRaw>;

const infoBlockSkinSize: SkinSize = 'm';
const colorConfig = createInfoColorConfig();

/**
 * Компонент для создания информационных блоков
 */
export const InfoBlock = memo<InfoBlockProps>(function InfoBlock({
    className,
    children,
    caption,
    color = 'island',
    onClose,
    buttons,
    'data-testid': dataTestId,
    closeButtonText,
    logNode,
    bottomText,
    hasShadow,
    labelBlock,
    help,
    ...props
}) {
    const { LogProvider } = useLogger('InfoBlock', logNode);

    const invertedTheme = useInvertedTheme();

    const sizeSkinClass = getSizeSkin(infoBlockSkinSize);
    const finalCloseButtonText = closeButtonText || i18n('Закрыть');
    const textSize: TextPresetWithSkin = caption ? 'current-main + current-body' : 'current-main';

    let iconComponent = colorConfig.icons[color] || colorConfig.icons.island;
    if (props.type === 'icon' && 'icon' in props && props.icon) {
        iconComponent = props.icon;
    }

    const { addonClasses } = useInternalVerticalAlignWithSkin({ textSize, addonType: 'icon' });

    const { iconSize: iconMainSize } = useTheme().sizeTokens[infoBlockSkinSize];
    const { iconSize: iconCloseSize } = useTheme().sizeTokens[infoBlockSkinSize];

    const colorSkinClass = getColorSkin(colorConfig.skinMap[color], {});

    const imageBlock = props.type === 'image' && 'imageSrc' in props && (
        <InfoBaseImage
            imageSrc={props.imageSrc}
            imageAlt={props.imageAlt}
            onClose={onClose}
            closeButtonText={finalCloseButtonText}
            iconSize={iconCloseSize}
            className={cls('imageContainer')}
            imageClassName={cls('image')}
            closeButtonClassName={cls('imageCloseButton')}
            dataTestId={dataTestId ? `${dataTestId}.CloseButton` : 'InfoBlock.CloseButton'}
        />
    );

    const illustrationBlock = props.type === 'illustration' && (
        <InfoBaseIllustration
            imageSrc={'imageSrc' in props ? props.imageSrc : undefined}
            imageAlt={'imageAlt' in props ? props.imageAlt : undefined}
            iconInCircle={'iconInCircle' in props ? props.iconInCircle : undefined}
            className={!isMobile() || !labelBlock ? cls('illustrationContainer') : undefined}
        />
    );

    const illustrationBlockWithLabel = props.type === 'illustration' && labelBlock && isMobile() && (
        <HStack className={cls('illustrationContainer')} justifyContent="space-between">
            {illustrationBlock}
            {labelBlock}
        </HStack>
    );

    const iconBlock = props.type === 'icon' && (
        <InfoBaseIcon
            icon={iconComponent}
            iconSize={iconMainSize}
            iconClassName={cls('iconLeft')}
            addonClasses={addonClasses}
            labelBlock={labelBlock}
            className={cls('iconContainer')}
        />
    );

    const textBlock = (
        <InfoBaseText
            textSize={textSize}
            caption={caption}
            help={help}
            labelBlock={labelBlock}
            showLabelInTextBlock={
                labelBlock ? isDesktop() || (props.type !== 'icon' && props.type !== 'illustration') : false
            }
            skinSize={infoBlockSkinSize}
            className={cls('text')}
            type={props.type}
        >
            {children}
        </InfoBaseText>
    );

    const buttonsBlock = buttons ? <InfoBaseButtons className={cls('buttons')}>{buttons}</InfoBaseButtons> : null;

    const bottomTextBlock = bottomText ? (
        <InfoBaseBottomText className={cls('bottomText')}>{bottomText}</InfoBaseBottomText>
    ) : null;

    const nonImageCloseButton = props.type !== 'image' && (
        <InfoBaseCloseButton
            onClose={onClose}
            closeButtonText={finalCloseButtonText}
            iconSize={iconCloseSize}
            variant="standard"
            className={cls('closeButton', [addonClasses])}
            iconClassName={cls('closeIcon')}
            dataTestId={dataTestId ? `${dataTestId}.CloseButton` : 'InfoBlock.CloseButton'}
        />
    );

    const ContainerComponent = isDesktop() ? HStack : VStack;
    const gap = getContainerGap();

    let content: ReactElement<{ className: string }> = isDesktop() ? (
        <HStack gap="current-geometry" className={cls('content')}>
            {textBlock}
            {nonImageCloseButton}
        </HStack>
    ) : (
        <VStack gap="16" className={cls('content')}>
            {illustrationBlockWithLabel || illustrationBlock}
            {iconBlock}
            {textBlock}
        </VStack>
    );

    if (isDesktop()) {
        if (buttonsBlock || bottomTextBlock) {
            content = (
                <VStack gap="12" className={cls('main-block')}>
                    {content}
                    {buttonsBlock}
                    {bottomTextBlock}
                </VStack>
            );
        }
    } else if (nonImageCloseButton) {
        content = (
            <HStack gap="8" className={cls('main-block')}>
                {content}
                {nonImageCloseButton}
            </HStack>
        );
    }

    content = (
        <ContainerComponent
            className={cls('container', {
                type: props.type ?? 'text',
                'has-shadow': hasShadow,
                'has-close-button': Boolean(onClose),
                'has-label': Boolean(labelBlock),
            })}
            gap={gap}
        >
            {isDesktop() ? (
                <>
                    {illustrationBlockWithLabel || illustrationBlock}
                    {iconBlock}
                    {content}
                </>
            ) : (
                <>
                    {content}
                    {buttonsBlock}
                    {bottomTextBlock}
                </>
            )}
        </ContainerComponent>
    );

    content = addImageBlockWrapper(imageBlock, content);

    return (
        <LogProvider>
            {addRootNodeClassName(
                content,
                cls({ color: color }, [
                    className,
                    colorSkinClass,
                    sizeSkinClass,
                    color === 'inverted' ? invertedTheme : undefined,
                ]),
                {
                    'data-testid': dataTestId || 'InfoBlock',
                },
            )}
        </LogProvider>
    );
});
